import React, { Component, ErrorInfo, ReactNode } from "react";
import {
  isAuthProviderDesyncError,
  isLikelyStaleChunkError,
  shouldAutoReloadForAuthDesync,
  shouldAutoReloadForStaleChunk,
} from "./utils/chunkLoadError";

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isAuthProviderDesyncError(error) && shouldAutoReloadForAuthDesync()) {
      window.location.reload();
      return;
    }

    if (isLikelyStaleChunkError(error) && shouldAutoReloadForStaleChunk()) {
      window.location.reload();
      return;
    }

    if (navigator.userAgent.match(/safari/i) && !navigator.userAgent.match(/chrome|chromium|crios/i)) {
      console.error("Uncaught error (Safari mode):", error.message);
    } else {
      console.error("Uncaught error:", error, errorInfo);
    }
  }

  public render() {
    if (this.state.hasError) {
      const staleChunk = isLikelyStaleChunkError(this.state.error);
      const authDesync = isAuthProviderDesyncError(this.state.error);
      return (
        <div style={{ padding: 20, background: 'white', color: 'black' }}>
          <h1>Something went wrong.</h1>
          {(staleChunk || authDesync) && (
            <p style={{ maxWidth: 520 }}>
              {authDesync
                ? 'This often happens in local development after a hot reload. Reload the page to restore app providers.'
                : 'This usually happens after the site was updated while this tab stayed open. Try reloading the page.'}
            </p>
          )}
          <p>
            <button
              type="button"
              style={{ padding: '8px 16px', cursor: 'pointer' }}
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </p>
          <pre style={{ whiteSpace: 'pre-wrap', color: 'red' }}>
            {this.state.error?.toString()}
          </pre>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 10 }}>
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}
